import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import {
  Archive,
  ArrowLeft,
  KeyRound,
  KeySquare,
  MailPlus,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react'
import {
  getTeacherAccountsListQueryKey,
  getTeacherAccountsRetrieveQueryKey,
  getV1ClassesListQueryKey,
  getV1ClassesRetrieveQueryKey,
  getV1MeRetrieveQueryOptions,
  getV1SchoolsRetrieveQueryKey,
  useTeacherAccountsArchive,
  useTeacherAccountsClassAdd,
  useTeacherAccountsClassRemove,
  useTeacherAccountsDelete,
  useTeacherAccountsPasswordReset,
  useTeacherAccountsResendInvitation,
  useTeacherAccountsRestore,
  useTeacherAccountsRetrieve,
  useTeacherAccountsSchoolAdd,
  useTeacherAccountsSchoolRemove,
  useV1ClassesList,
  useV1MeRetrieve,
  useV1SchoolsList,
} from '@/api/client'
import { V1SchoolsListState } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { AvatarPortrait } from '@/components/profile/dicebear-avatar'
import { avatarSeed } from '@/lib/avatar-catalog'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ConfirmDialog } from '@/components/admin-accounts/confirm-dialog'
import { TemporaryPasswordDialog } from '@/components/admin-accounts/temporary-password-dialog'
import { canSetTeacherTemporaryPassword, formatExpiry, isPast } from '@/lib/temporary-password'
import { LookupTruncationNote } from '@/components/lookup-truncation-note'
import { DeleteTeacherDialog } from '@/components/teachers/teacher-actions-dialog'
import { TeacherForm } from '@/components/teachers/teacher-form'
import { firstBlockingMessage, restoreNotice } from '@/components/teachers/relation-errors'
import {
  invitationStateLabel,
  invitationStateTone,
  teacherStatusLabel,
  teacherStatusTone,
} from '@/lib/teacher-labels'

type DetailSearch = { zaproszenie?: 'niewyslane' }

export const Route = createFileRoute('/_panel/nauczyciele/$nauczycielId')({
  // TR-700..707 R1: operators only — teachers are bounced like on every
  // sibling admin route.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role === 'teacher') {
      throw redirect({ to: '/' })
    }
  },
  // `?zaproszenie=niewyslane` — set by the create form when the invitation
  // e-mail failed to send (TR-700): the detail shows the retry banner.
  validateSearch: (search: Record<string, unknown>): DetailSearch =>
    search.zaproszenie === 'niewyslane' ? { zaproszenie: 'niewyslane' } : {},
  component: TeacherDetailPage,
})

function TeacherDetailPage() {
  const { nauczycielId } = Route.useParams()
  const id = Number(nauczycielId)
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const { zaproszenie } = Route.useSearch()

  const [editing, setEditing] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [tempPasswordOpen, setTempPasswordOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  // Post-action operator notices (mail failure, last assignment removed…).
  const [notice, setNotice] = useState<string | null>(null)
  const [relationError, setRelationError] = useState<string | null>(null)

  const query = useTeacherAccountsRetrieve(id)
  const teacher = query.data?.status === 200 ? query.data.data : undefined
  const isActive = teacher?.status === 'active'
  const isArchived = teacher?.status === 'archived'

  const meQuery = useV1MeRetrieve()
  const isSuperadmin = meQuery.data?.status === 200 && meQuery.data.data.role === 'superadmin'

  const archiveMutation = useTeacherAccountsArchive()
  const restoreMutation = useTeacherAccountsRestore()
  const deleteMutation = useTeacherAccountsDelete()
  const resetMutation = useTeacherAccountsPasswordReset()
  const resendMutation = useTeacherAccountsResendInvitation()
  const schoolAddMutation = useTeacherAccountsSchoolAdd()
  const schoolRemoveMutation = useTeacherAccountsSchoolRemove()
  const classAddMutation = useTeacherAccountsClassAdd()
  const classRemoveMutation = useTeacherAccountsClassRemove()

  // Pickers (TR-707 / TR-705). Schools: active minus already assigned —
  // filtered client-side (no dedicated backend filter for this direction).
  const schoolsQuery = useV1SchoolsList(
    { state: V1SchoolsListState.active, page_size: 100 },
    { query: { enabled: !!teacher && isActive } },
  )
  const activeSchools = schoolsQuery.data?.status === 200 ? schoolsQuery.data.data.results : []
  const schoolsCount = schoolsQuery.data?.status === 200 ? schoolsQuery.data.data.count : 0
  const assignedIds = new Set((teacher?.schools ?? []).map((s) => s.id))
  const assignableSchools = activeSchools.filter((s) => !assignedIds.has(s.id))

  const classesQuery = useV1ClassesList(
    { eligible_for_teacher: id, page_size: 100 },
    { query: { enabled: !!teacher && isActive } },
  )
  const eligibleClasses = classesQuery.data?.status === 200 ? classesQuery.data.data.results : []
  const eligibleClassesCount = classesQuery.data?.status === 200 ? classesQuery.data.data.count : 0

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: getTeacherAccountsRetrieveQueryKey(id) })
    await queryClient.invalidateQueries({ queryKey: getTeacherAccountsListQueryKey() })
    // The eligible-classes picker is a classes-LIST variant — without this it
    // keeps offering a just-linked class (and hides newly eligible ones).
    await queryClient.invalidateQueries({ queryKey: getV1ClassesListQueryKey() })
  }

  const runArchive = async () => {
    setActionError(null)
    const response = await archiveMutation.mutateAsync({ id })
    if (response.status >= 400) {
      setActionError('Nie udało się zarchiwizować konta. Spróbuj ponownie.')
      return
    }
    await refresh()
    setArchiving(false)
    if (response.status === 200 && response.data.notification_email_sent === false) {
      setNotice(
        'Konto zarchiwizowane, ale powiadomienie e-mail do nauczyciela nie zostało wysłane.',
      )
    }
  }

  const runRestore = async () => {
    setActionError(null)
    const response = await restoreMutation.mutateAsync({ id })
    if (response.status >= 400) {
      // 403 = brak uprawnień (tylko superadmin), 400 = konto nie jest
      // zarchiwizowane (np. odświeżone w innej karcie).
      setActionError('Nie udało się przywrócić konta. Odśwież stronę i spróbuj ponownie.')
      return
    }
    await refresh()
    setRestoring(false)
    if (response.status === 200) {
      // Restore zawsze wraca „pustym" kontem (przypisania zerwane przy
      // archiwizacji nie są wskrzeszane) — operator musi je najpierw przypisać
      // do szkoły. Kompozycja notatki (+ ewentualne ostrzeżenie o mailu) siedzi
      // w czystym helperze, żeby dało się ją przetestować bez DOM-u.
      setNotice(restoreNotice(response.data.notification_email_sent))
    }
  }

  const runDelete = async () => {
    if (!teacher) return
    setActionError(null)
    const response = await deleteMutation.mutateAsync({
      id,
      data: { confirm_email: teacher.email },
    })
    if (response.status >= 400) {
      setActionError('Nie udało się usunąć konta. Spróbuj ponownie.')
      return
    }
    await queryClient.invalidateQueries({ queryKey: getTeacherAccountsListQueryKey() })
    setDeleting(false)
    navigate({ to: '/nauczyciele' })
  }

  const runReset = async () => {
    setActionError(null)
    const response = await resetMutation.mutateAsync({ id })
    if (response.status >= 400) {
      setActionError('Nie udało się zainicjować resetu hasła. Spróbuj ponownie.')
      return
    }
    setResetting(false)
    setNotice('Wiadomość z linkiem do ustawienia nowego hasła została wysłana.')
  }

  const runResend = async () => {
    setNotice(null)
    const response = await resendMutation.mutateAsync({ id })
    if (response.status >= 400) {
      setNotice('Nie udało się ponowić zaproszenia. Spróbuj ponownie.')
      return
    }
    await refresh()
    if (response.status === 200) {
      if (response.data.invitation_email_sent && zaproszenie) {
        // Clear the stale create-time failure flag from the URL.
        navigate({ search: {}, replace: true })
      }
      setNotice(
        response.data.invitation_email_sent
          ? 'Zaproszenie zostało wysłane ponownie.'
          : 'Zaproszenie wystawione, ale wysyłka e-maila nie powiodła się — spróbuj ponownie.',
      )
    }
  }

  const addSchool = async (schoolId: number) => {
    setRelationError(null)
    const response = await schoolAddMutation.mutateAsync({ id, data: { school_id: schoolId } })
    if (response.status >= 400) {
      setRelationError('Nie udało się dodać przypisania do szkoły. Spróbuj ponownie.')
      return
    }
    await refresh()
    await queryClient.invalidateQueries({ queryKey: getV1SchoolsRetrieveQueryKey(schoolId) })
  }

  const removeSchool = async (schoolId: number) => {
    setRelationError(null)
    setNotice(null)
    const response = await schoolRemoveMutation.mutateAsync({ id, schoolId: String(schoolId) })
    if (response.status === 409) {
      // TR-707 R7: class links in this school must be removed first.
      setRelationError(firstBlockingMessage(response.data))
      return
    }
    if (response.status >= 400) {
      setRelationError('Nie udało się odebrać przypisania. Spróbuj ponownie.')
      return
    }
    await refresh()
    await queryClient.invalidateQueries({ queryKey: getV1SchoolsRetrieveQueryKey(schoolId) })
    if (response.status === 200 && response.data.last_assignment_removed) {
      // TR-707 R8: the account stays active with no school assignment.
      setNotice(
        'Konto pozostało bez żadnego przypisania szkolnego — do czasu dodania nowego ' +
          'przypisania nie można powiązać go z klasą. Zalecane: dodaj przypisanie albo ' +
          'zarchiwizuj konto.',
      )
    }
  }

  const addClass = async (classId: number) => {
    setRelationError(null)
    const response = await classAddMutation.mutateAsync({ id, data: { class_id: classId } })
    if (response.status >= 400) {
      setRelationError('Nie udało się dodać powiązania z klasą. Spróbuj ponownie.')
      return
    }
    await refresh()
    await queryClient.invalidateQueries({ queryKey: getV1ClassesRetrieveQueryKey(classId) })
  }

  const removeClass = async (classId: number) => {
    setRelationError(null)
    const response = await classRemoveMutation.mutateAsync({ id, classId: String(classId) })
    if (response.status >= 400) {
      setRelationError('Nie udało się odebrać powiązania. Spróbuj ponownie.')
      return
    }
    await refresh()
    await queryClient.invalidateQueries({ queryKey: getV1ClassesRetrieveQueryKey(classId) })
  }

  const relationPending =
    schoolAddMutation.isPending ||
    schoolRemoveMutation.isPending ||
    classAddMutation.isPending ||
    classRemoveMutation.isPending

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Nauczyciel" />
        <p className="p-6 text-sm text-muted-foreground">Ładowanie…</p>
      </>
    )
  }
  if (!teacher) {
    return (
      <>
        <PageHeader title="Nauczyciel" />
        <p className="p-6 text-sm text-muted-foreground">Nie znaleziono konta nauczyciela.</p>
      </>
    )
  }

  if (editing) {
    return (
      <>
        <PageHeader
          title={
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4" /> {teacher.last_name} {teacher.first_name}
            </button>
          }
        />
        <div className="p-6">
          <h2 className="mb-6 text-lg font-semibold">Edycja danych konta</h2>
          <TeacherForm teacher={teacher} onClose={() => setEditing(false)} />
        </div>
      </>
    )
  }

  const invitation = teacher.invitation
  const showResend = isActive && invitation != null && invitation.state !== 'used'
  const canSetTemporaryPassword = canSetTeacherTemporaryPassword(
    isSuperadmin,
    teacher.status,
    teacher.has_password,
  )
  const tempExpiresAt = teacher.temporary_password_expires_at

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon" aria-label="Wróć do listy nauczycieli">
              <Link to="/nauczyciele">
                <ArrowLeft />
              </Link>
            </Button>
            {/* alt pusty: nazwisko jest tytułem tego widoku. */}
            <AvatarPortrait
              options={teacher.avatar}
              seed={avatarSeed(teacher.email)}
              alt=""
              size="md"
            />
            {teacher.last_name} {teacher.first_name}
            <StatusBadge tone={teacherStatusTone[teacher.status]}>
              {teacherStatusLabel[teacher.status]}
            </StatusBadge>
            {invitation && invitation.state !== 'used' && (
              <StatusBadge tone={invitationStateTone[invitation.state]}>
                {invitationStateLabel[invitation.state]}
              </StatusBadge>
            )}
          </span>
        }
        actions={
          <div className="flex gap-2">
            {showResend && (
              <Button
                size="sm"
                variant="outline"
                onClick={runResend}
                disabled={resendMutation.isPending}
              >
                <MailPlus /> Wyślij ponownie zaproszenie
              </Button>
            )}
            {isActive && (
              <Button size="sm" variant="outline" onClick={() => setResetting(true)}>
                <KeyRound /> Reset hasła
              </Button>
            )}
            {canSetTemporaryPassword && (
              <Button size="sm" variant="outline" onClick={() => setTempPasswordOpen(true)}>
                <KeySquare /> Ustaw hasło tymczasowe
              </Button>
            )}
            {isActive && (
              <Button size="sm" variant="outline" onClick={() => setArchiving(true)}>
                <Archive /> Archiwizuj
              </Button>
            )}
            {/* Restore = mirror of archive: only for an archived account, and
                superadmin-only (feature 027, matching the delete guard). */}
            {isArchived && isSuperadmin && (
              <Button size="sm" variant="outline" onClick={() => setRestoring(true)}>
                <RotateCcw /> Przywróć
              </Button>
            )}
            {isSuperadmin && (
              <Button size="sm" variant="destructive" onClick={() => setDeleting(true)}>
                <Trash2 /> Usuń
              </Button>
            )}
            {isActive && (
              <Button size="sm" onClick={() => setEditing(true)}>
                <Pencil /> Edytuj
              </Button>
            )}
          </div>
        }
      />

      <ConfirmDialog
        open={archiving}
        onOpenChange={(open) => {
          setArchiving(open)
          if (!open) setActionError(null)
        }}
        title="Archiwizacja konta nauczyciela"
        description={
          <span className="flex flex-col gap-2">
            <span>Po archiwizacji konta:</span>
            <span className="flex flex-col gap-1 text-sm">
              <span>• logowanie i odzyskiwanie hasła przestaną być możliwe,</span>
              <span>
                • konto zniknie z domyślnego widoku listy (pozostanie pod filtrem statusu),
              </span>
              <span>
                • wszystkie powiązania z klasami ({teacher.class_count}) i przypisania do szkół (
                {teacher.school_count}) zostaną zerwane,
              </span>
              <span>• dane konta i historia działań zostaną zachowane.</span>
            </span>
            {teacher.sole_coordinator_class_count > 0 && (
              <span>
                Konto jest jedynym koordynatorem{' '}
                <span className="font-medium">
                  {teacher.sole_coordinator_class_count}{' '}
                  {teacher.sole_coordinator_class_count === 1 ? 'klasy' : 'klas'}
                </span>{' '}
                — obsługa ról koordynatora w tych klasach przejdzie do administratorów.
              </span>
            )}
          </span>
        }
        confirmLabel="Archiwizuj"
        pending={archiveMutation.isPending}
        error={archiving ? actionError : null}
        onConfirm={runArchive}
      />

      <ConfirmDialog
        open={restoring}
        onOpenChange={(open) => {
          setRestoring(open)
          if (!open) setActionError(null)
        }}
        title="Przywrócenie konta nauczyciela"
        description={
          <span className="flex flex-col gap-2">
            <span>Po przywróceniu konta:</span>
            <span className="flex flex-col gap-1 text-sm">
              <span>• konto wróci jako aktywne i znów pojawi się na domyślnej liście,</span>
              <span>
                • powiązania z klasami i przypisania do szkół zerwane przy archiwizacji NIE zostaną
                przywrócone — konto wraca puste,
              </span>
              <span>• aby móc powiązać je z klasami, najpierw przypisz konto do szkoły,</span>
              <span>
                • nauczyciel odzyska dostęp przez reset hasła lub nowe zaproszenie (samo
                przywrócenie nie loguje go z powrotem).
              </span>
            </span>
          </span>
        }
        confirmLabel="Przywróć"
        pending={restoreMutation.isPending}
        error={restoring ? actionError : null}
        onConfirm={runRestore}
      />

      <ConfirmDialog
        open={resetting}
        onOpenChange={(open) => {
          setResetting(open)
          if (!open) setActionError(null)
        }}
        title="Reset hasła nauczyciela"
        description={
          `Nauczyciel otrzyma na adres ${teacher.email} wiadomość z jednorazowym, ` +
          'ograniczonym czasowo linkiem do ustawienia nowego hasła. Nowe hasło nie jest ' +
          'widoczne dla operatora.'
        }
        confirmLabel="Wyślij link resetu"
        pending={resetMutation.isPending}
        error={resetting ? actionError : null}
        onConfirm={runReset}
      />

      <TemporaryPasswordDialog
        kind="teacher"
        targetId={teacher.id}
        targetEmail={teacher.email}
        open={tempPasswordOpen}
        onOpenChange={setTempPasswordOpen}
        onDone={refresh}
        onSessionEnded={() => window.location.assign('/login?expired=ok')}
      />

      <DeleteTeacherDialog
        open={deleting}
        onOpenChange={(open) => {
          setDeleting(open)
          if (!open) setActionError(null)
        }}
        email={teacher.email}
        soleCoordinatorClassCount={teacher.sole_coordinator_class_count}
        pending={deleteMutation.isPending}
        error={deleting ? actionError : null}
        onConfirm={runDelete}
      />

      <div className="space-y-4 p-6">
        {zaproszenie === 'niewyslane' && showResend && (
          <NoticeBanner>
            Konto zostało założone, ale zaproszenie e-mail nie zostało wysłane. Użyj akcji „Wyślij
            ponownie zaproszenie”.
          </NoticeBanner>
        )}
        {notice && <NoticeBanner>{notice}</NoticeBanner>}

        <Card>
          <CardContent className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            {(
              [
                ['E-mail (login)', teacher.email],
                ['Telefon', teacher.phone],
                ['Tytuł / stanowisko', teacher.title || '—'],
                ['Utworzono', new Date(teacher.created_at).toLocaleDateString('pl-PL')],
                ...(tempExpiresAt
                  ? ([
                      [
                        'Hasło tymczasowe',
                        isPast(tempExpiresAt)
                          ? 'hasło tymczasowe wygasło'
                          : `hasło tymczasowe ważne do ${formatExpiry(tempExpiresAt)}`,
                      ],
                    ] as const)
                  : []),
              ] as const
            ).map(([label, value]) => (
              <div key={label}>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="mt-0.5 font-medium">{value}</p>
              </div>
            ))}
            <div className="col-span-2 sm:col-span-4">
              <p className="text-xs text-muted-foreground">Notatki administracyjne</p>
              <p className="mt-0.5 whitespace-pre-wrap">{teacher.admin_notes || '—'}</p>
            </div>
          </CardContent>
        </Card>

        {relationError && <p className="text-sm text-destructive">{relationError}</p>}

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Szkoły ({teacher.school_count})</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              {teacher.schools.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Konto nie ma żadnego przypisania szkolnego.
                </p>
              )}
              {teacher.schools.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-2 text-sm">
                  <Link
                    to="/szkoly/$szkolaId"
                    params={{ szkolaId: String(s.id) }}
                    className="font-medium text-brand hover:underline"
                  >
                    {s.name}
                  </Link>
                  {isActive && (
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Odbierz przypisanie do szkoły"
                      disabled={relationPending}
                      onClick={() => removeSchool(s.id)}
                    >
                      <X />
                    </Button>
                  )}
                </div>
              ))}
              {isActive && (
                <Select
                  value=""
                  onValueChange={(v) => addSchool(Number(v))}
                  disabled={relationPending}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Dodaj przypisanie do szkoły…" />
                  </SelectTrigger>
                  <SelectContent>
                    {assignableSchools.length === 0 && (
                      <p className="px-2 py-1.5 text-xs text-muted-foreground">
                        Brak aktywnych szkół, do których konto nie jest jeszcze przypisane.
                      </p>
                    )}
                    {assignableSchools.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name} — {s.city}
                      </SelectItem>
                    ))}
                    <LookupTruncationNote count={schoolsCount} shown={activeSchools.length} />
                  </SelectContent>
                </Select>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Klasy ({teacher.class_count})</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              {teacher.classes.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nauczyciel nie jest koordynatorem żadnej klasy.
                </p>
              )}
              {teacher.classes.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 text-sm">
                  <span>
                    <Link
                      to="/klasy/$klasaId"
                      params={{ klasaId: String(c.id) }}
                      className="font-medium text-brand hover:underline"
                    >
                      {c.name}
                    </Link>{' '}
                    <span className="text-muted-foreground">· {c.school_name}</span>
                  </span>
                  {isActive && (
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Odbierz powiązanie z klasą"
                      disabled={relationPending}
                      onClick={() => removeClass(c.id)}
                    >
                      <X />
                    </Button>
                  )}
                </div>
              ))}
              {isActive && (
                <Select
                  value=""
                  onValueChange={(v) => addClass(Number(v))}
                  disabled={relationPending}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Powiąż z klasą…" />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleClasses.length === 0 && (
                      <p className="px-2 py-1.5 text-xs text-muted-foreground">
                        We wszystkich przypisanych szkołach nie ma aktywnych klas bez tego
                        powiązania. Powiązania operują wyłącznie na klasach ze szkół, do których
                        nauczyciel jest przypisany.
                      </p>
                    )}
                    {eligibleClasses.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name} — {c.school_name}
                      </SelectItem>
                    ))}
                    <LookupTruncationNote
                      count={eligibleClassesCount}
                      shown={eligibleClasses.length}
                    />
                  </SelectContent>
                </Select>
              )}
              {isActive && teacher.school_count === 0 && (
                <p className="text-xs text-muted-foreground">
                  <Plus className="mr-1 inline size-3" />
                  Najpierw dodaj przypisanie do szkoły — powiązania z klasami wymagają przypisania
                  szkolnego (TR-707).
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

function NoticeBanner({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-3.5 dark:border-amber-900 dark:bg-amber-950">
      <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
      <p className="flex-1 text-sm text-amber-800 dark:text-amber-200">{children}</p>
    </div>
  )
}
